/**
 * /admin/people — the user-management surface (polish task 04). Replaces the
 * Settings→Users tab. All lifecycle actions go through mariejois
 * UserManagementService (authoritative), so role/status/password changes
 * actually affect login + authz. A user's groups and access grants render
 * inline in the detail drawer, with the jargon-free grant CTA.
 */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, Button, Dialog, DialogContent, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Sheet, SheetContent, SheetHeader, SheetTitle, SpecraCheckboxField, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { MoreHorizontal, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { GrantAssignmentDialog } from '../Access/GrantAssignmentDialog';
import { RevokeConfirmDialog } from '../Access/RevokeConfirmDialog';
import { GrantDialog } from '../GrantDialog';
import { ResolvedId, useAdminNames } from '../nameResolution';
import { useAssignmentsForUser, useCreateUser, useDeleteUser, useGroupsForUser, useListUsers, useResendInvite, useResetPassword, useUpdateUserRole, useUpdateUserStatus } from './hooks';
// Roles an admin may assign from the console. Mirrors mariejois validRoles.
// Wave 3c (authz rebuild D3): the baseline vocabulary collapsed to
// internal / admin / member — business access comes from bundle grants,
// so member is the only non-privileged baseline.
const ROLE_OPTIONS = [
    'admin',
    'internal',
    'member'
];
// internal (Specra staff) crosses tenants, so only staff may grant or change
// it (SAD-947). Mirrors mariejois mayManageInternal; the backend enforces.
function useRoleOptions() {
    const { user } = useAuth();
    const staff = (user?.role ?? '').toLowerCase() === 'internal';
    return {
        options: staff ? ROLE_OPTIONS : ROLE_OPTIONS.filter((r)=>r !== 'internal'),
        staff
    };
}
function roleOf(u) {
    return u.roles?.[0] ?? 'member';
}
export function PeoplePage() {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [createOpen, setCreateOpen] = useState(false);
    const [detailUser, setDetailUser] = useState(null);
    const [grantUser, setGrantUser] = useState(null);
    const [delegateFrom, setDelegateFrom] = useState(null);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const { data, isLoading } = useListUsers(search, page);
    const updateRole = useUpdateUserRole();
    const updateStatus = useUpdateUserStatus();
    const resetPassword = useResetPassword();
    const resendInvite = useResendInvite();
    const deleteUser = useDeleteUser();
    const users = data?.users ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        "data-testid": Testids.adminPeople.page,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "People"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Create users, manage roles and access. Signups land at the minimum level; elevate here."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Button, {
                        "data-testid": Testids.adminPeople.createButton,
                        onClick: ()=>setCreateOpen(true),
                        children: [
                            /*#__PURE__*/ _jsx(UserPlus, {
                                className: "mr-2 h-4 w-4"
                            }),
                            "Add user"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Input, {
                "data-testid": Testids.adminPeople.searchInput,
                placeholder: "Search by name or email",
                value: search,
                onChange: (e)=>{
                    setSearch(e.target.value);
                    setPage(1);
                },
                className: "max-w-sm"
            }),
            /*#__PURE__*/ _jsxs(Table, {
                "data-testid": Testids.adminPeople.table,
                children: [
                    /*#__PURE__*/ _jsx(TableHeader, {
                        children: /*#__PURE__*/ _jsxs(TableRow, {
                            children: [
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Name"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Email"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Role"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Status"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    className: "w-10"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(TableBody, {
                        children: [
                            users.map((u)=>/*#__PURE__*/ _jsxs(TableRow, {
                                    "data-testid": Testids.adminPeople.row,
                                    "data-row-id": u.id,
                                    className: "cursor-pointer",
                                    onClick: ()=>setDetailUser(u),
                                    children: [
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "font-medium",
                                            children: u.name
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            children: u.email
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                variant: "outline",
                                                children: roleOf(u)
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            children: /*#__PURE__*/ _jsx(Badge, {
                                                variant: u.active ? 'default' : 'secondary',
                                                children: u.active ? 'Active' : 'Inactive'
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            onClick: (e)=>e.stopPropagation(),
                                            children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                                        asChild: true,
                                                        children: /*#__PURE__*/ _jsx(Button, {
                                                            variant: "ghost",
                                                            size: "icon",
                                                            children: /*#__PURE__*/ _jsx(MoreHorizontal, {
                                                                className: "h-4 w-4"
                                                            })
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                                        align: "end",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                "data-testid": Testids.adminPeople.statusAction,
                                                                onClick: ()=>updateStatus.mutate({
                                                                        userId: u.id,
                                                                        active: !u.active
                                                                    }),
                                                                children: u.active ? 'Deactivate' : 'Activate'
                                                            }),
                                                            u.active && /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                "data-testid": Testids.adminPeople.resendInviteAction,
                                                                onClick: ()=>resendInvite.mutate({
                                                                        userId: u.id,
                                                                        email: u.email
                                                                    }),
                                                                children: "Resend invite"
                                                            }),
                                                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                "data-testid": Testids.adminPeople.resetPasswordAction,
                                                                onClick: ()=>{
                                                                    const pw = window.prompt('New password (min 8 chars)');
                                                                    if (pw && pw.length >= 8) {
                                                                        resetPassword.mutate({
                                                                            userId: u.id,
                                                                            newPassword: pw
                                                                        });
                                                                    } else if (pw !== null) {
                                                                        toast.error('Password must be at least 8 characters');
                                                                    }
                                                                },
                                                                children: "Reset password"
                                                            }),
                                                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                                                "data-testid": Testids.adminPeople.deleteAction,
                                                                className: "text-destructive",
                                                                onClick: ()=>{
                                                                    if (window.confirm(`Deactivate and remove ${u.email}?`)) {
                                                                        deleteUser.mutate({
                                                                            userId: u.id
                                                                        });
                                                                    }
                                                                },
                                                                children: "Remove user"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                }, u.id)),
                            !isLoading && users.length === 0 && /*#__PURE__*/ _jsx(TableRow, {
                                children: /*#__PURE__*/ _jsx(TableCell, {
                                    colSpan: 5,
                                    className: "text-center text-muted-foreground",
                                    children: "No users found"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CreateUserDialog, {
                open: createOpen,
                onOpenChange: setCreateOpen
            }),
            detailUser && /*#__PURE__*/ _jsx(UserDetailDrawer, {
                user: detailUser,
                open: !!detailUser,
                onOpenChange: (o)=>!o && setDetailUser(null),
                onChangeRole: (role)=>updateRole.mutate({
                        userId: detailUser.id,
                        role
                    }),
                onGrant: ()=>setGrantUser(detailUser),
                onDelegate: (a)=>setDelegateFrom({
                        ownerUserId: a.userId,
                        roleBundleKey: a.roleBundleKey,
                        scopeNodeId: a.scopeNodeId
                    }),
                onRevoke: (a)=>setRevokeTarget(a)
            }),
            grantUser && /*#__PURE__*/ _jsx(GrantDialog, {
                open: !!grantUser,
                onOpenChange: (o)=>!o && setGrantUser(null),
                userId: grantUser.id,
                principalLabel: grantUser.name || grantUser.email
            }),
            /*#__PURE__*/ _jsx(GrantAssignmentDialog, {
                open: !!delegateFrom,
                onOpenChange: (o)=>!o && setDelegateFrom(null),
                delegateFrom: delegateFrom
            }),
            revokeTarget && /*#__PURE__*/ _jsx(RevokeConfirmDialog, {
                open: true,
                onOpenChange: (o)=>!o && setRevokeTarget(null),
                assignmentId: revokeTarget.id,
                userId: revokeTarget.userId,
                bundleDisplayName: revokeTarget.roleBundleDisplayName || revokeTarget.roleBundleKey
            })
        ]
    });
}
const EMPTY_FORM = {
    name: '',
    email: '',
    password: '',
    role: 'member',
    sendInvite: true
};
export function CreateUserDialog({ open, onOpenChange }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const create = useCreateUser();
    const { options: roleOptions } = useRoleOptions();
    // Inviting, the person picks their own password from the email link.
    const valid = form.name && form.email && (form.sendInvite || form.password.length >= 8);
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminPeople.createDialog,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsx(DialogTitle, {
                        children: "Add user"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "create-user-name",
                                    children: "Name"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "create-user-name",
                                    "data-testid": Testids.adminPeople.createNameInput,
                                    value: form.name,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            name: e.target.value
                                        })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "create-user-email",
                                    children: "Email"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "create-user-email",
                                    "data-testid": Testids.adminPeople.createEmailInput,
                                    type: "email",
                                    value: form.email,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            email: e.target.value
                                        })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                            "data-testid": Testids.adminPeople.createInviteToggle,
                            label: "Email them an invite",
                            description: "They get a link to set their own password. It works once and expires in 72 hours.",
                            controlProps: {
                                checked: form.sendInvite,
                                onCheckedChange: (checked)=>setForm({
                                        ...form,
                                        sendInvite: checked === true
                                    })
                            }
                        }),
                        !form.sendInvite && /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "create-user-password",
                                    children: "Temporary password"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    id: "create-user-password",
                                    "data-testid": Testids.adminPeople.createPasswordInput,
                                    type: "password",
                                    placeholder: "min 8 characters",
                                    value: form.password,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            password: e.target.value
                                        })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Role"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: form.role,
                                    onValueChange: (role)=>setForm({
                                            ...form,
                                            role
                                        }),
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.adminPeople.createRoleSelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: roleOptions.map((r)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: r,
                                                    children: r
                                                }, r))
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminPeople.createSubmit,
                            disabled: !valid || create.isPending,
                            onClick: async ()=>{
                                await create.mutateAsync({
                                    ...form,
                                    password: form.sendInvite ? '' : form.password
                                });
                                setForm(EMPTY_FORM);
                                onOpenChange(false);
                            },
                            children: form.sendInvite ? 'Send invite' : 'Create'
                        })
                    ]
                })
            ]
        })
    });
}
function UserDetailDrawer({ user, open, onOpenChange, onChangeRole, onGrant, onDelegate, onRevoke }) {
    const { data: groups } = useGroupsForUser(user.id, open);
    const { data: assignments } = useAssignmentsForUser(user.id, open);
    const { options: roleOptions, staff } = useRoleOptions();
    const lockedStaffAccount = roleOf(user) === 'internal' && !staff;
    const names = useAdminNames();
    return /*#__PURE__*/ _jsx(Sheet, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(SheetContent, {
            side: "right",
            className: "flex w-full flex-col overflow-y-auto sm:max-w-md",
            "data-testid": Testids.adminPeople.detailDrawer,
            children: [
                /*#__PURE__*/ _jsx(SheetHeader, {
                    children: /*#__PURE__*/ _jsx(SheetTitle, {
                        children: user.name || user.email
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-4 space-y-6 text-sm",
                    children: [
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-muted-foreground",
                                    children: "Email"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    children: user.email
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Role"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: user.roles?.[0] ?? 'user',
                                    onValueChange: onChangeRole,
                                    disabled: lockedStaffAccount,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: roleOptions.map((r)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: r,
                                                    children: r
                                                }, r))
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-muted-foreground",
                                    children: "Groups"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap gap-1.5",
                                    children: [
                                        (groups ?? []).map((g)=>/*#__PURE__*/ _jsx(Badge, {
                                                variant: "secondary",
                                                children: g.displayName
                                            }, g.id)),
                                        (groups ?? []).length === 0 && /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "None"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: "Access grants"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: "outline",
                                            "data-testid": Testids.adminPeople.grantButton,
                                            onClick: onGrant,
                                            children: "Grant access"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        (assignments ?? []).map((a)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between gap-2 rounded-md border px-3 py-2",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "min-w-0",
                                                        children: [
                                                            /*#__PURE__*/ _jsxs("div", {
                                                                className: "font-medium",
                                                                children: [
                                                                    /*#__PURE__*/ _jsx(ResolvedId, {
                                                                        id: a.roleBundleKey,
                                                                        name: names.bundle(a.roleBundleKey)
                                                                    }),
                                                                    a.assignmentType !== 'primary' && /*#__PURE__*/ _jsx(Badge, {
                                                                        variant: "outline",
                                                                        className: "ml-1.5",
                                                                        children: a.assignmentType
                                                                    })
                                                                ]
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs text-muted-foreground",
                                                                children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                                    id: a.scopeNodeId,
                                                                    name: names.scope(a.scopeNodeId)
                                                                })
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex shrink-0 gap-1",
                                                        children: [
                                                            a.assignmentType !== 'designee' && /*#__PURE__*/ _jsx(Button, {
                                                                variant: "ghost",
                                                                size: "sm",
                                                                className: "text-xs",
                                                                "data-testid": Testids.adminPeople.delegateAction,
                                                                title: "Time-boxed designee grant of this role at this scope",
                                                                onClick: ()=>onDelegate(a),
                                                                children: "Delegate…"
                                                            }),
                                                            /*#__PURE__*/ _jsx(Button, {
                                                                variant: "ghost",
                                                                size: "sm",
                                                                className: "text-xs text-destructive",
                                                                "data-testid": Testids.adminPeople.revokeAction,
                                                                onClick: ()=>onRevoke(a),
                                                                children: "Revoke"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }, a.id)),
                                        (assignments ?? []).length === 0 && /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "No grants yet"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
